/* ==========================================================================
   Motion layer.
   Everything here is IntersectionObserver or event-driven. No scroll listeners,
   no rAF loops, no scroll-hijacking. The audience for this page is anxious;
   taking control of their scroll would work against the promise of the product.
   ========================================================================== */

(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------
     Scroll reveal
     Elements marked .reveal fade up once, staggered by data-stagger
     within a shared parent.
     --------------------------------------------------------------- */
  function initReveal() {
    var targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;

    if (prefersReduced.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------
     Staggered groups
     A parent with [data-stagger] assigns an increasing delay to its
     .reveal children so lists arrive in sequence, not all at once.
     --------------------------------------------------------------- */
  function initStagger() {
    document.querySelectorAll('[data-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-stagger'), 10) || 80;
      var children = group.querySelectorAll('.reveal');
      children.forEach(function (child, i) {
        child.style.setProperty('--reveal-delay', i * step + 'ms');
      });
    });
  }

  /* ---------------------------------------------------------------
     Header border on scroll, via a sentinel element rather than a
     scroll listener.
     --------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header || !('IntersectionObserver' in window)) return;

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(sentinel);

    var observer = new IntersectionObserver(
      function (entries) {
        header.classList.toggle('is-stuck', !entries[0].isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
  }

  /* ---------------------------------------------------------------
     FAQ accordion. Animates height for a smooth open, collapses to
     instant under reduced motion. One panel open at a time.
     --------------------------------------------------------------- */
  function initFaq() {
    var buttons = document.querySelectorAll('.faq__q');
    if (!buttons.length) return;

    function close(btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.setAttribute('aria-expanded', 'false');
      if (prefersReduced.matches) {
        panel.style.height = '0px';
        return;
      }
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () {
        panel.style.height = '0px';
      });
    }

    function open(btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.setAttribute('aria-expanded', 'true');
      if (prefersReduced.matches) {
        panel.style.height = 'auto';
        return;
      }
      panel.style.height = panel.scrollHeight + 'px';
      panel.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'height') return;
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', handler);
      });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        buttons.forEach(function (other) {
          if (other !== btn && other.getAttribute('aria-expanded') === 'true') close(other);
        });
        if (isOpen) {
          close(btn);
        } else {
          open(btn);
        }
      });
    });

    // Keep an open panel correctly sized if the viewport reflows.
    window.addEventListener('resize', function () {
      buttons.forEach(function (btn) {
        if (btn.getAttribute('aria-expanded') !== 'true') return;
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.style.height = 'auto';
      });
    });
  }

  /* ---------------------------------------------------------------
     Hero crossfade fallback.
     Where scroll timelines are supported and motion is allowed, CSS
     scrubs the crossfade on its own and this does nothing. Otherwise a
     sentinel below the fold flips the pair once, and the CSS transition
     carries it. Either way: no scroll listener.
     --------------------------------------------------------------- */
  function initHeroCrossfade() {
    var media = document.querySelector('.hero__breath-media');
    if (!media || !('IntersectionObserver' in window)) return;

    var hasScrollTimeline =
      window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()');

    // CSS already owns this case.
    if (hasScrollTimeline && !prefersReduced.matches) return;

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText =
      'position:absolute;top:22vh;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(sentinel);

    var observer = new IntersectionObserver(
      function (entries) {
        media.classList.toggle('is-after', !entries[0].isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
  }

  /* ---------------------------------------------------------------
     Product picker.
     Singles and the pack are mutually exclusive: buying the pack plus a
     single would mean paying twice for the same file. Singles are capped
     so that wanting all three pushes you to the pack, which is the point.
     At least one item always stays selected, so the button never submits
     an empty cart.
     --------------------------------------------------------------- */
  function initPicker() {
    document.querySelectorAll('[data-picker]').forEach(function (form) {
      var singles = [].slice.call(form.querySelectorAll('[data-role="single"]'));
      var pack = form.querySelector('[data-role="pack"]');
      var totalEl = form.querySelector('[data-total]');
      var hintEl = form.querySelector('[data-hint]');
      var slots = form.querySelector('[data-cart-slots]');
      var max = parseInt(form.getAttribute('data-max'), 10) || 2;
      var currency = form.getAttribute('data-currency') || 'EUR';
      var homeCurrency = form.getAttribute('data-home-currency') || 'EUR';
      // El codigo viaja en la URL del checkout: asi llega aplicado sin que
      // nadie tenga que copiarlo a mano, que es donde se perdian las ventas.
      var discount = form.getAttribute('data-discount') || '';
      var checkoutUrl = '/checkout' + (discount ? '?discount=' + encodeURIComponent(discount) : '');
      var baseHint = hintEl ? hintEl.textContent : '';

      // Fuera del mercado de casa el importe se marca con el codigo ISO, igual
      // que hace el snippet money.liquid: el mismo simbolo de dolar es dolar en
      // Estados Unidos y peso en Mexico, y esa duda se paga en el carrito.
      function money(cents) {
        var away = currency !== homeCurrency;
        var locale = away && currency === 'USD' ? 'en-US' : document.documentElement.lang || 'es';
        var out;
        try {
          out = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency
          }).format(cents / 100);
        } catch (e) {
          out = (cents / 100).toFixed(2) + ' ' + currency;
        }
        if (away && out.indexOf(currency) === -1) out += '\u00A0' + currency;
        return out;
      }

      function chosen() {
        if (pack && pack.checked) return [pack];
        return singles.filter(function (c) { return c.checked; });
      }

      function sync(changed) {
        // Pack and singles never coexist.
        if (changed === pack && pack.checked) {
          singles.forEach(function (c) { c.checked = false; });
        } else if (changed && changed !== pack && changed.checked && pack) {
          pack.checked = false;
        }

        var picked = singles.filter(function (c) { return c.checked; });

        // Nothing left selected: fall back to the first item rather than
        // letting the customer submit an empty cart. This has to fire for
        // every path in, including unchecking the pack.
        if (!picked.length && !(pack && pack.checked) && singles.length) {
          singles[0].checked = true;
          picked = [singles[0]];
        }

        // Cap reached: block the remaining singles instead of silently
        // dropping the customer's last click.
        var atCap = picked.length >= max && !(pack && pack.checked);
        singles.forEach(function (c) {
          var lock = atCap && !c.checked;
          c.disabled = lock;
          c.closest('.pick').classList.toggle('is-blocked', lock);
          c.closest('.pick').classList.toggle('is-on', c.checked);
        });
        if (pack) {
          pack.closest('.pick').classList.toggle('is-on', pack.checked);
        }

        if (hintEl) {
          hintEl.textContent = atCap
            ? hintEl.getAttribute('data-full') || baseHint
            : baseHint;
        }

        // Se suman los precios de TARIFA y después se aplica el descuento
        // al pedido entero, truncando los céntimos. Es exactamente lo que
        // hace Shopify, así que el total de aquí y el de la caja coinciden
        // al céntimo. Aplicarlo artículo a artículo desviaba 1-2 céntimos.
        var lista = chosen().reduce(function (acc, c) {
          return acc + (parseInt(c.getAttribute('data-price'), 10) || 0);
        }, 0);
        var pct = parseInt(form.getAttribute('data-pct'), 10) || 0;
        var sum = lista - Math.floor(lista * pct / 100);
        if (totalEl) totalEl.textContent = money(sum);
      }

      singles.forEach(function (c) {
        c.addEventListener('change', function () { sync(c); });
      });
      if (pack) pack.addEventListener('change', function () { sync(pack); });

      // These are digital files: a second copy of the same title is worth
      // nothing to the buyer and generates refund requests. So the cart is
      // emptied and rebuilt on every purchase, which guarantees exactly one
      // of each selected item no matter how many times the button is used.
      form.addEventListener('submit', function (e) {
        var picked = chosen();
        if (!picked.length) { e.preventDefault(); return; }

        var payload = picked.map(function (c) {
          return { id: Number(c.value), quantity: 1 };
        });

        if (!window.fetch) {
          // No fetch: fall back to the plain form post. Indices are rebuilt
          // here so they stay sequential even if the middle item was skipped.
          slots.innerHTML = '';
          picked.forEach(function (c, idx) {
            [['id', c.value], ['quantity', '1']].forEach(function (pair) {
              var el = document.createElement('input');
              el.type = 'hidden';
              el.name = 'items[' + idx + '][' + pair[0] + ']';
              el.value = pair[1];
              slots.appendChild(el);
            });
          });
          return;
        }

        e.preventDefault();
        var btn = form.querySelector('[data-submit]');
        if (btn) {
          btn.disabled = true;
          btn.classList.add('is-busy');
        }

        fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: payload })
            });
          })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            window.location.href = checkoutUrl;
          })
          .catch(function () {
            // Let the normal form post try instead of leaving a dead button.
            if (btn) {
              btn.disabled = false;
              btn.classList.remove('is-busy');
            }
            form.submit();
          });
      });

      sync(null);
    });
  }

  function init() {
    initStagger();
    initReveal();
    initHeader();
    initFaq();
    initHeroCrossfade();
    initPicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ── Barra fija de compra ───────────────────────────────────────────────
   Se esconde mientras esté a la vista cualquiera de las zonas declaradas
   (la portada y la sección de compra). Se usa IntersectionObserver en vez
   de escuchar el scroll: el navegador avisa solo y no hay cálculo en cada
   píxel, que es lo que da tirones en móvil.

   Se guarda QUÉ zonas están visibles, no cuántas. Con un contador, la
   primera llamada del observador —que informa de todas las zonas a la vez—
   sumaba por la portada y restaba por la sección de compra, y el contador
   volvía a cero: la barra no se escondía nunca. */
(function () {
  var barra = document.querySelector('[data-buybar]');
  if (!barra) return;

  var sel = (barra.getAttribute('data-oculta-en') || '#comprar')
    .split(',').map(function (s) { return s.trim(); }).filter(Boolean);

  var zonas = [];
  sel.forEach(function (s) {
    try { [].push.apply(zonas, document.querySelectorAll(s)); } catch (e) {}
  });
  // Sin zonas que vigilar no hay nada que esconder: se muestra y punto.
  if (!zonas.length) { barra.classList.add('is-on'); barra.setAttribute('aria-hidden', 'false'); return; }

  // Lista de zonas visibles ahora mismo. Cada entrada entra o sale por su
  // propia cuenta, así que no hay forma de descuadrarla.
  var dentro = [];
  function marca(el, visible) {
    var i = dentro.indexOf(el);
    if (visible && i === -1) dentro.push(el);
    else if (!visible && i !== -1) dentro.splice(i, 1);
  }

  function pinta() {
    var mostrar = dentro.length === 0;
    barra.classList.toggle('is-on', mostrar);
    barra.setAttribute('aria-hidden', mostrar ? 'false' : 'true');
  }

  if (!('IntersectionObserver' in window)) { pinta(); return; }

  var obs = new IntersectionObserver(function (entradas) {
    entradas.forEach(function (e) { marca(e.target, e.isIntersecting); });
    pinta();
  }, {
    // Un margen negativo arriba evita que la barra reaparezca por un par
    // de píxeles de la zona asomando por el borde.
    rootMargin: '-10% 0px -10% 0px',
    threshold: 0
  });

  zonas.forEach(function (z) { obs.observe(z); });
  pinta();
})();

/* ── En móvil, los botones de compra aterrizan en el producto ───────────
   Todos apuntan a #comprar, que es el principio de la sección: en un móvil
   eso deja la pantalla llena de titular y texto, y el producto queda fuera
   de vista. Aquí se desvía el salto unos píxeles más abajo, al selector
   Digital/Físico, para que lo primero que se vea sea lo que se compra.

   En escritorio no se toca: ahí cabe la sección entera y el titular hace
   su trabajo de presentar.

   Se intercepta en el documento en vez de en cada botón: así vale para los
   cinco que existen hoy (portada, cabecera, cierre, pie y barra fija) y
   para cualquiera que se añada mañana sin tener que acordarse de esto. */
(function () {
  var MOVIL = 760;

  function destino() {
    return document.querySelector('.offer__buy') || document.getElementById('comprar');
  }

  /* Se usa scrollIntoView y no aritmética a mano: la hoja de estilos ya
     declara `scroll-padding-top` con la altura de la cabecera, así que el
     navegador deja el elemento justo debajo él solo. Calcularlo aquí
     duplicaba ese número y se desincronizaría al cambiar la cabecera. */
  function lleva() {
    if (window.innerWidth > MOVIL) return false;
    var t = destino();
    if (!t) return false;
    t.scrollIntoView({ block: 'start' });
    return true;
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    // Vale tanto "#comprar" como "/#comprar" desde el pie.
    if (href.indexOf('#comprar') === -1) return;
    // Si el enlace apunta a otra página, se deja ir: allí no hay nada que ajustar.
    if (href.charAt(0) === '/' && location.pathname !== '/') return;

    if (lleva()) {
      e.preventDefault();
      try { history.replaceState(null, '', '#comprar'); } catch (err) {}
    }
  });

  // Llegadas directas con la ancla puesta (por ejemplo desde el pie de otra página).
  if (location.hash === '#comprar') {
    window.addEventListener('load', function () { setTimeout(lleva, 120); });
  }
})();
